import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

// Quarter labels for 3 years
export const QUARTERS = [
  'Q1 2026', 'Q2 2026', 'Q3 2026', 'Q4 2026',
  'Q1 2027', 'Q2 2027', 'Q3 2027', 'Q4 2027',
  'Q1 2028', 'Q2 2028', 'Q3 2028', 'Q4 2028',
];

// Types
export interface StaffPosition {
  id: string;
  name: string;
  baseSalary: number;
  yearlyIncreasePercent: number;
  fteByQuarter: number[];
}

export interface RevenueSettings {
  b2cMonthlyFee: number[];
  b2cSubsByQuarter: number[];
  b2bFeePerStudent: number[];
  b2bSchoolsByQuarter: number[];
  avgStudentsPerSchool: number[];
}

export interface CostSettings {
  cloudHostingPerUser: number;
  aiTokenPerUser: number;
  paymentProcessingRate: number;
  fixedCloudCosts: number[];
  fixedAiCosts: number[];
  officeExpenses: number[];
  softwareSubscriptions: number[];
  legalAccounting: number[];
  b2cCostPerAcquisition: number[];
  b2bOutreach: number[];
}

export interface QuarterData {
  quarter: string;
  b2cRevenue: number;
  b2bRevenue: number;
  totalRevenue: number;
  totalCogs: number;
  grossMargin: number;
  staffCosts: number;
  otherOpEx: number;
  totalOpEx: number;
  netRevenue: number;
  b2cSubs: number;
  b2bSchools: number;
  totalStudents: number;
  newSubs: number;
  digitalAdvertising: number;
  cashPosition: number;
}

export interface YearSummary {
  year: string;
  period: string;
  b2cSubsStart: number;
  b2cSubsEnd: number;
  b2cMonthlyFee: number;
  b2cRevenue: number;
  b2bSchoolsStart: number;
  b2bSchoolsEnd: number;
  b2bStudentsEnd: number;
  b2bFeePerStudent: number;
  b2bRevenue: number;
  totalRevenue: number;
  grossMargin: number;
  netRevenue: number;
  cumulativePosition: number;
}

export interface FinancialCalculations {
  quarterData: QuarterData[];
  cashOutQuarter: string | null;
  needsBridgeRound: boolean;
  bridgeAmount: number;
  y1Total: number;
  y2Total: number;
  y3Total: number;
  totalRevenue: number;
  totalNet: number;
  finalCash: number;
  yearSummaries: YearSummary[];
}

interface FinancialModelContextType {
  seedInvestment: number;
  setSeedInvestment: (value: number) => void;
  staffPositions: StaffPosition[];
  setStaffPositions: React.Dispatch<React.SetStateAction<StaffPosition[]>>;
  revenueSettings: RevenueSettings;
  setRevenueSettings: React.Dispatch<React.SetStateAction<RevenueSettings>>;
  costSettings: CostSettings;
  setCostSettings: React.Dispatch<React.SetStateAction<CostSettings>>;
  calculations: FinancialCalculations;
}

const FinancialModelContext = createContext<FinancialModelContextType | null>(null);

// Default values
const defaultStaffPositions: StaffPosition[] = [
  { id: 'ceo', name: 'CEO', baseSalary: 180000, yearlyIncreasePercent: 3.5, fteByQuarter: [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1] },
  { id: 'head-eng', name: 'Head of Engineering', baseSalary: 180000, yearlyIncreasePercent: 5, fteByQuarter: [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1] },
  { id: 'head-sales', name: 'Head of Sales', baseSalary: 120000, yearlyIncreasePercent: 5, fteByQuarter: [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1] },
  { id: 'senior-dev', name: 'Senior Developer', baseSalary: 160000, yearlyIncreasePercent: 3.5, fteByQuarter: [0, 0, 0, 0, 0, 0.5, 0.75, 1, 2, 2, 2, 2] },
  { id: 'mid-dev', name: 'Mid Tier Developer', baseSalary: 150000, yearlyIncreasePercent: 3.5, fteByQuarter: [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1] },
  { id: 'uat-content', name: 'UAT / Content', baseSalary: 50000, yearlyIncreasePercent: 3.5, fteByQuarter: [0, 0, 1, 1, 1, 1, 1, 1, 2.5, 2.5, 2.5, 2.5] },
  { id: 'b2b-sales', name: 'B2B Sales Rep', baseSalary: 110000, yearlyIncreasePercent: 3.5, fteByQuarter: [0, 0, 0, 0, 0, 0, 1, 1, 4, 4, 4, 4] },
  { id: 'admin', name: 'General & Admin', baseSalary: 10000, yearlyIncreasePercent: 0, fteByQuarter: [0, 0, 0, 0, 1, 1, 1, 1, 3, 3, 3, 3] },
  { id: 'devops', name: 'DevOps', baseSalary: 75000, yearlyIncreasePercent: 3.5, fteByQuarter: [0, 0, 0, 0, 0, 1, 1, 1, 2.5, 2.5, 2.5, 2.5] },
  { id: 'support', name: 'Support', baseSalary: 40000, yearlyIncreasePercent: 3.5, fteByQuarter: [0, 0, 0, 0, 1, 1.5, 2, 2, 4, 4, 4, 4] },
];

const defaultRevenueSettings: RevenueSettings = {
  b2cMonthlyFee: [25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25],
  b2cSubsByQuarter: [0, 0, 0, 0, 0, 700, 1800, 4300, 5000, 5500, 6000, 10000],
  b2bFeePerStudent: [0, 0, 0, 0, 10, 10, 10, 10, 10, 10, 10, 10],
  b2bSchoolsByQuarter: [0, 0, 1, 3, 3, 8, 13, 18, 48, 53, 58, 68],
  avgStudentsPerSchool: [500, 500, 500, 500, 500, 500, 500, 500, 500, 500, 500, 500],
};

const defaultCostSettings: CostSettings = {
  cloudHostingPerUser: 0.65,
  aiTokenPerUser: 0.15,
  paymentProcessingRate: 2.2,
  fixedCloudCosts: [500, 500, 1000, 1000, 0, 0, 0, 0, 0, 0, 0, 0],
  fixedAiCosts: [1000, 1000, 1000, 1000, 0, 0, 0, 0, 0, 0, 0, 0],
  officeExpenses: [0, 0, 3000, 3000, 15000, 15000, 15000, 15000, 22000, 22000, 22000, 22000],
  softwareSubscriptions: [0, 0, 5000, 5000, 20000, 20000, 20000, 20000, 25000, 25000, 25000, 25000],
  legalAccounting: [0, 0, 0, 3000, 15000, 15000, 15000, 15000, 26000, 26000, 26000, 26000],
  b2cCostPerAcquisition: [0, 0, 65, 65, 50, 50, 50, 50, 30, 30, 30, 30],
  b2bOutreach: [0, 0, 0, 3000, 5000, 5000, 5000, 5000, 10000, 10000, 10000, 10000],
};

export const FinancialModelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [seedInvestment, setSeedInvestment] = useState(500000);
  const [staffPositions, setStaffPositions] = useState<StaffPosition[]>(defaultStaffPositions);
  const [revenueSettings, setRevenueSettings] = useState<RevenueSettings>(defaultRevenueSettings);
  const [costSettings, setCostSettings] = useState<CostSettings>(defaultCostSettings);

  const calculations = useMemo(() => {
    const quarterData = QUARTERS.map((quarter, i) => {
      // Revenue calculations
      const b2cSubs = revenueSettings.b2cSubsByQuarter[i];
      const b2cFee = revenueSettings.b2cMonthlyFee[i];
      const b2cRevenue = b2cSubs * b2cFee * 3;

      const b2bSchools = revenueSettings.b2bSchoolsByQuarter[i];
      const studentsPerSchool = revenueSettings.avgStudentsPerSchool[i];
      const b2bFee = revenueSettings.b2bFeePerStudent[i];
      const totalStudents = b2bSchools * studentsPerSchool;
      const b2bRevenue = totalStudents * b2bFee * 3;

      const totalRevenue = b2cRevenue + b2bRevenue;

      // COGS calculations
      const year = Math.floor(i / 4);
      const totalUsers = b2cSubs + totalStudents;

      let cloudCost = costSettings.fixedCloudCosts[i];
      let aiCost = costSettings.fixedAiCosts[i];
      if (year >= 1) {
        cloudCost = totalUsers * costSettings.cloudHostingPerUser;
        aiCost = totalUsers * costSettings.aiTokenPerUser;
      }
      const paymentProcessing = totalRevenue * (costSettings.paymentProcessingRate / 100);
      const totalCogs = cloudCost + aiCost + paymentProcessing;

      // Staff costs
      const staffCosts = staffPositions.reduce((sum, pos) => {
        const adjustedSalary = pos.baseSalary * Math.pow(1 + pos.yearlyIncreasePercent / 100, year);
        const quarterlySalary = adjustedSalary / 4;
        return sum + (quarterlySalary * pos.fteByQuarter[i]);
      }, 0);

      // Calculate new subscribers this quarter (for digital advertising spend)
      const prevSubs = i > 0 ? revenueSettings.b2cSubsByQuarter[i - 1] : 0;
      const newSubs = Math.max(0, b2cSubs - prevSubs);
      const digitalAdvertising = newSubs * costSettings.b2cCostPerAcquisition[i];

      // Other OpEx
      const otherOpEx =
        costSettings.officeExpenses[i] +
        costSettings.softwareSubscriptions[i] +
        costSettings.legalAccounting[i] +
        digitalAdvertising +
        costSettings.b2bOutreach[i];

      const totalOpEx = staffCosts + otherOpEx;
      const grossMargin = totalRevenue - totalCogs;
      const netRevenue = grossMargin - totalOpEx;

      return {
        quarter,
        b2cRevenue,
        b2bRevenue,
        totalRevenue,
        totalCogs,
        grossMargin,
        staffCosts,
        otherOpEx,
        totalOpEx,
        netRevenue,
        b2cSubs,
        b2bSchools,
        totalStudents,
        newSubs,
        digitalAdvertising,
        cashPosition: 0, // Will be calculated below
      };
    });

    // Calculate cash position over time
    let cashPosition = seedInvestment;
    let cashOutQuarter: string | null = null;
    let minCash = seedInvestment;

    const cashFlow = quarterData.map((q) => {
      cashPosition += q.netRevenue;
      if (cashPosition < minCash) minCash = cashPosition;
      if (cashPosition <= 0 && !cashOutQuarter) {
        cashOutQuarter = q.quarter;
      }
      return { ...q, cashPosition };
    });

    const needsBridgeRound = minCash < 0;
    const bridgeAmount = needsBridgeRound ? Math.ceil(Math.abs(minCash) / 100000) * 100000 : 0;

    // Yearly totals
    const y1Total = cashFlow.slice(0, 4).reduce((s, q) => s + q.netRevenue, 0);
    const y2Total = cashFlow.slice(4, 8).reduce((s, q) => s + q.netRevenue, 0);
    const y3Total = cashFlow.slice(8, 12).reduce((s, q) => s + q.netRevenue, 0);
    const totalRevenue = cashFlow.reduce((s, q) => s + q.totalRevenue, 0);
    const totalNet = cashFlow.reduce((s, q) => s + q.netRevenue, 0);

    // Year summaries for Slide 4
    const yearSummaries: YearSummary[] = [
      {
        year: 'Year 1',
        period: '2026',
        b2cSubsStart: revenueSettings.b2cSubsByQuarter[0],
        b2cSubsEnd: revenueSettings.b2cSubsByQuarter[3],
        b2cMonthlyFee: revenueSettings.b2cMonthlyFee[3],
        b2cRevenue: cashFlow.slice(0, 4).reduce((s, q) => s + q.b2cRevenue, 0),
        b2bSchoolsStart: revenueSettings.b2bSchoolsByQuarter[0],
        b2bSchoolsEnd: revenueSettings.b2bSchoolsByQuarter[3],
        b2bStudentsEnd: revenueSettings.b2bSchoolsByQuarter[3] * revenueSettings.avgStudentsPerSchool[3],
        b2bFeePerStudent: revenueSettings.b2bFeePerStudent[3],
        b2bRevenue: cashFlow.slice(0, 4).reduce((s, q) => s + q.b2bRevenue, 0),
        totalRevenue: cashFlow.slice(0, 4).reduce((s, q) => s + q.totalRevenue, 0),
        grossMargin: cashFlow.slice(0, 4).reduce((s, q) => s + q.grossMargin, 0),
        netRevenue: y1Total,
        cumulativePosition: cashFlow[3].cashPosition,
      },
      {
        year: 'Year 2',
        period: '2027',
        b2cSubsStart: revenueSettings.b2cSubsByQuarter[4],
        b2cSubsEnd: revenueSettings.b2cSubsByQuarter[7],
        b2cMonthlyFee: revenueSettings.b2cMonthlyFee[7],
        b2cRevenue: cashFlow.slice(4, 8).reduce((s, q) => s + q.b2cRevenue, 0),
        b2bSchoolsStart: revenueSettings.b2bSchoolsByQuarter[4],
        b2bSchoolsEnd: revenueSettings.b2bSchoolsByQuarter[7],
        b2bStudentsEnd: revenueSettings.b2bSchoolsByQuarter[7] * revenueSettings.avgStudentsPerSchool[7],
        b2bFeePerStudent: revenueSettings.b2bFeePerStudent[7],
        b2bRevenue: cashFlow.slice(4, 8).reduce((s, q) => s + q.b2bRevenue, 0),
        totalRevenue: cashFlow.slice(4, 8).reduce((s, q) => s + q.totalRevenue, 0),
        grossMargin: cashFlow.slice(4, 8).reduce((s, q) => s + q.grossMargin, 0),
        netRevenue: y2Total,
        cumulativePosition: cashFlow[7].cashPosition,
      },
      {
        year: 'Year 3',
        period: '2028',
        b2cSubsStart: revenueSettings.b2cSubsByQuarter[8],
        b2cSubsEnd: revenueSettings.b2cSubsByQuarter[11],
        b2cMonthlyFee: revenueSettings.b2cMonthlyFee[11],
        b2cRevenue: cashFlow.slice(8, 12).reduce((s, q) => s + q.b2cRevenue, 0),
        b2bSchoolsStart: revenueSettings.b2bSchoolsByQuarter[8],
        b2bSchoolsEnd: revenueSettings.b2bSchoolsByQuarter[11],
        b2bStudentsEnd: revenueSettings.b2bSchoolsByQuarter[11] * revenueSettings.avgStudentsPerSchool[11],
        b2bFeePerStudent: revenueSettings.b2bFeePerStudent[11],
        b2bRevenue: cashFlow.slice(8, 12).reduce((s, q) => s + q.b2bRevenue, 0),
        totalRevenue: cashFlow.slice(8, 12).reduce((s, q) => s + q.totalRevenue, 0),
        grossMargin: cashFlow.slice(8, 12).reduce((s, q) => s + q.grossMargin, 0),
        netRevenue: y3Total,
        cumulativePosition: cashFlow[11].cashPosition,
      },
    ];

    return {
      quarterData: cashFlow,
      cashOutQuarter,
      needsBridgeRound,
      bridgeAmount,
      y1Total,
      y2Total,
      y3Total,
      totalRevenue,
      totalNet,
      finalCash: cashFlow[11]?.cashPosition || 0,
      yearSummaries,
    };
  }, [seedInvestment, staffPositions, revenueSettings, costSettings]);

  return (
    <FinancialModelContext.Provider
      value={{
        seedInvestment,
        setSeedInvestment,
        staffPositions,
        setStaffPositions,
        revenueSettings,
        setRevenueSettings,
        costSettings,
        setCostSettings,
        calculations,
      }}
    >
      {children}
    </FinancialModelContext.Provider>
  );
};

export const useFinancialModel = () => {
  const context = useContext(FinancialModelContext);
  if (!context) {
    throw new Error('useFinancialModel must be used within a FinancialModelProvider');
  }
  return context;
};
